import { useSyncExternalStore } from 'react'

/**
 * Source archive overlay — localStorage v1.
 *
 * Datafabric device cleanup (docs/fabric-overview-requirements.md §Device
 * cleanup): a decommissioned device is either DELETED (the backend call, once
 * capture C1 passes) or ARCHIVED locally. Archived devices are hidden on
 * Sources and Overview; the archive lives in this browser only. In demo mode
 * a delete is echoed into the same overlay so the device disappears from the
 * demo read (`applyDeletedOverlay`).
 *
 * THIS SHAPE IS A DRAFT API CONTRACT: `archived` is the console-side hide
 * list a server-side archive would replace; `deleted` is the demo echo of the
 * device delete.
 *
 * Storage: ONE key per cluster+scope, `bloo.sources.<clusterId>.<scopeId>`,
 * holding `{ v: 1, archived: { [device_address]: archivedAt }, deleted:
 * { [device_address]: deletedAt } }` (epoch ms). House localStorage rules
 * (cf. extractor-store, storage-store): try/catch everywhere, SSR-safe, a
 * corrupt or wrong-version blob is superseded by an empty overlay (and by the
 * next write) rather than crashing the page.
 */

export interface SourceOverlay {
  v: 1
  /** device_address → archivedAt (epoch ms). */
  archived: Record<string, number>
  /** device_address → deletedAt (epoch ms). */
  deleted: Record<string, number>
}

export const SOURCE_KEY_PREFIX = 'bloo.sources.'

export function sourceOverlayKey(clusterId: string, scopeId: string): string {
  return `${SOURCE_KEY_PREFIX}${clusterId}.${scopeId}`
}

export interface SourceOverlayStore {
  /** Stable snapshot until the next effective mutation. */
  read: () => SourceOverlay
  /** Hide devices in this browser. Idempotent: an already-archived address keeps its first archivedAt. */
  archive: (addresses: string[], at?: number) => void
  /** Show devices again. */
  unarchive: (addresses: string[]) => void
  /** Echo of the device delete. Also removes the address from `archived`; keeps the first deletedAt. */
  markDeleted: (addresses: string[], at?: number) => void
  subscribe: (cb: () => void) => () => void
}

const EMPTY: SourceOverlay = Object.freeze({
  v: 1,
  archived: Object.freeze({}) as Record<string, number>,
  deleted: Object.freeze({}) as Record<string, number>,
}) as SourceOverlay

function readStampMap(v: unknown): Record<string, number> {
  const out: Record<string, number> = {}
  if (!v || typeof v !== 'object' || Array.isArray(v)) return out
  for (const [address, at] of Object.entries(v as Record<string, unknown>)) {
    if (typeof at === 'number' && Number.isFinite(at)) out[address] = at
  }
  return out
}

/** Parse a stored blob defensively: anything but a v1 object is an empty overlay. */
function readSourceOverlay(parsed: unknown): SourceOverlay {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return EMPTY
  const root = parsed as Record<string, unknown>
  if (root.v !== 1) return EMPTY
  return { v: 1, archived: readStampMap(root.archived), deleted: readStampMap(root.deleted) }
}

class LocalSourceOverlayStore implements SourceOverlayStore {
  private cache: SourceOverlay | null = null
  private listeners = new Set<() => void>()
  constructor(private readonly key: string) {}

  read = (): SourceOverlay => {
    if (this.cache) return this.cache
    let parsed: unknown
    try {
      const raw = globalThis.localStorage?.getItem(this.key)
      parsed = raw ? JSON.parse(raw) : null
    } catch {
      parsed = null
    }
    this.cache = readSourceOverlay(parsed)
    return this.cache
  }

  archive = (addresses: string[], at: number = Date.now()) => {
    const cur = this.read()
    const fresh = addresses.filter((a) => !(a in cur.archived))
    if (fresh.length === 0) return
    const archived = { ...cur.archived }
    for (const a of fresh) archived[a] = at
    this.write({ ...cur, archived })
  }

  unarchive = (addresses: string[]) => {
    const cur = this.read()
    const present = addresses.filter((a) => a in cur.archived)
    if (present.length === 0) return
    const archived = { ...cur.archived }
    for (const a of present) delete archived[a]
    this.write({ ...cur, archived })
  }

  markDeleted = (addresses: string[], at: number = Date.now()) => {
    const cur = this.read()
    const changes = addresses.filter((a) => !(a in cur.deleted) || a in cur.archived)
    if (changes.length === 0) return
    const archived = { ...cur.archived }
    const deleted = { ...cur.deleted }
    for (const a of changes) {
      delete archived[a]
      if (!(a in deleted)) deleted[a] = at
    }
    this.write({ v: 1, archived, deleted })
  }

  reset = () => {
    try {
      globalThis.localStorage?.removeItem(this.key)
    } catch {
      /* no storage: nothing to drop */
    }
    this.cache = EMPTY
    this.notify()
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => {
      this.listeners.delete(cb)
    }
  }

  private write(next: SourceOverlay) {
    try {
      globalThis.localStorage?.setItem(this.key, JSON.stringify(next))
    } catch {
      /* quota or no storage: keep the in-memory overlay so the UI still reflects the action */
    }
    this.cache = next
    this.notify()
  }

  private notify() {
    for (const cb of this.listeners) cb()
  }
}

const stores = new Map<string, LocalSourceOverlayStore>()

/** One store per cluster+scope, shared by every hook and mutation on the page. */
export function sourceStore(clusterId: string, scopeId: string): SourceOverlayStore {
  const key = sourceOverlayKey(clusterId, scopeId)
  let s = stores.get(key)
  if (!s) {
    s = new LocalSourceOverlayStore(key)
    stores.set(key, s)
  }
  return s
}

/** Live view of the overlay for one scope; the reference is stable until the blob changes. */
export function useSourceOverlay(clusterId: string, scopeId: string): SourceOverlay {
  const store = sourceStore(clusterId, scopeId)
  return useSyncExternalStore(store.subscribe, store.read, store.read)
}

export function archivedSet(overlay: SourceOverlay): Set<string> {
  return new Set(Object.keys(overlay.archived))
}

export function deletedSet(overlay: SourceOverlay): Set<string> {
  return new Set(Object.keys(overlay.deleted))
}

/**
 * The demo read with deleted devices dropped. Order and identity of the
 * surviving rows are preserved; with nothing deleted the input array itself
 * is returned. Archived devices are NOT dropped — hiding them is the page's call.
 */
export function applyDeletedOverlay<T extends { device_address: string }>(devices: T[], overlay: SourceOverlay): T[] {
  const gone = overlay.deleted
  if (Object.keys(gone).length === 0) return devices
  return devices.filter((d) => !(d.device_address in gone))
}

/** Demo seeder body: forget every source overlay in this browser. Idempotent. */
export function resetAllSourceOverlays(): void {
  try {
    const ls = globalThis.localStorage
    if (ls) {
      for (let i = ls.length - 1; i >= 0; i--) {
        const key = ls.key(i)
        if (key && key.startsWith(SOURCE_KEY_PREFIX)) ls.removeItem(key)
      }
    }
  } catch {
    /* no storage */
  }
  for (const s of stores.values()) s.reset()
}
